'use client';

import { EmptyState, ErrorState, Heading, Pagination, Stack, StackItem } from '@pie/amber';
import { useClientTable, DESCENDING } from '@upms/use-client-table';
import { useMemo } from 'react';
import { IssuedSearchPolicy, useSearchPolicies } from '@/hooks/useSearchPolicies';
import { PolicyCard, PolicyCardSkeleton } from './PolicyCard';
import { StatusFilter } from './StatusFilter';
import { usePolicyStatusFilter } from '@/hooks/usePolicyStatusFilter';

const PAGE_SIZE = 5;

type PolicyListProps = {
  policies: IssuedSearchPolicy[];
  status: ReturnType<typeof useSearchPolicies>['status'];
};

const PolicyList = ({ policies, status }: PolicyListProps) => {
  const { rows, pagination } = useClientTable<IssuedSearchPolicy>({
    data: policies,
    columns: [{ key: 'effectiveDate', header: 'Term' }],
    initialPageSize: PAGE_SIZE,
    initialSortKey: 'effectiveDate',
    initialSortDirection: DESCENDING,
  });

  if (status === 'error') {
    return (
      <ErrorState
        heading="Failed to load"
        helpText="We were unable to load your policies. Please try again later."
        size="md"
      />
    );
  }

  if (status === 'pending') {
    return <PolicyCardSkeleton />;
  }

  if (status === 'success' && policies.length === 0) {
    return (
      <EmptyState heading="No policies match" helpText="Please try a different filter." size="md" />
    );
  }

  return (
    <Stack gap="layout-2xs">
      <Stack gap="layout-xs">
        {rows.map((policy) => (
          <PolicyCard key={policy.policyId} policy={policy} />
        ))}
      </Stack>
      {pagination.totalRows > PAGE_SIZE && (
        <Stack direction="row" justify="end">
          <Pagination
            layout="numbered"
            onPageChange={pagination.goTo}
            page={pagination.pageNumber}
            pages={pagination.pageCount}
          />
        </Stack>
      )}
    </Stack>
  );
};

export const YourPolicies = () => {
  const { data, status } = useSearchPolicies();
  const { handleValueChange, isMatch, key, options, value } = usePolicyStatusFilter();

  // Derived in render — never copied into state (docs/agents/state.md). The memo
  // is not an optimization: it keeps the array referentially stable across
  // unrelated re-renders so useClientTable's autoResetPageIndex doesn't snap the
  // page back to 1 whenever `data` changes identity.
  const policies = data?.results;
  // Derived in render — never copied into state (docs/agents/state.md). The memo
  // is not an optimization: it keeps the array referentially stable across
  // unrelated re-renders so useClientTable's autoResetPageIndex doesn't snap the
  // page back to 1 whenever `data` changes identity.
  const filteredPolicies = useMemo(() => {
    if (policies === undefined) return [];
    if (value.length === 0) return policies;
    return policies.filter(isMatch);
  }, [policies, isMatch, value]);

  return (
    <Stack gap="layout-xs">
      <Stack align="center" direction="row" gap="layout-xs">
        <StackItem grow>
          <Heading onContrast="default" size="lg">
            Your Policies
          </Heading>
        </StackItem>
        {policies && policies.length > PAGE_SIZE && (
          <StackItem>
            <StatusFilter onChangeAction={handleValueChange} options={options} value={value} />
          </StackItem>
        )}
      </Stack>
      <PolicyList
        // Filter changes remount the list so useClientTable re-seeds from page 1.
        // TODO: this is a deficiency with the useClientTable filter
        key={key}
        policies={filteredPolicies}
        status={status}
      />
    </Stack>
  );
};
